/**
 * La geometría de la pila de hojas de la tarjeta de un documento.
 *
 * Vive acá y no en el componente porque **la web dibuja la misma pila** en la previsualización de
 * la app. Es matemática pura: no sabe de React ni de React Native, y devolver la rotación como
 * `transform: [{ rotate }]` es sólo la forma en que la consume el estilo nativo — la web lee ese
 * mismo ángulo y lo escribe como `rotate(…)` en CSS.
 */

/** Grados de rotación por cada hoja del fondo. */
export const STEP_ANGLE = 8
/**
 * Alto de la hoja delantera como fracción del contenedor: TODO el alto.
 *
 * Hasta el 2026-10-28 era `0.9` y el documento quedaba ~10–15 pt más abajo que la tarjeta de
 * imagen de al lado, que llena el cuadro de arriba a abajo. El diseño pide que la hoja delantera
 * comparta borde superior e inferior con esa tarjeta; una carta (≈ 0.77 de ancho) a alto completo
 * deja el resto del cuadro, a la derecha, para que asomen las hojas rotadas del fondo.
 */
export const PAGE_HEIGHT = 1
/**
 * Proporción carta (ancho/alto). Es el DEFAULT, no la única forma posible.
 *
 * Hasta el 2026-09-10 toda hoja se dibujaba con esta proporción y la miniatura se recortaba contra
 * ella con `cover`: un A4 perdía los costados y un plano apaisado salía irreconocible. Hoy la forma
 * real viaja en el contrato (`thumbnailAspect`) y esto es a lo que se cae cuando no se sabe.
 */
export const PAGE_ASPECT = 8.5 / 11
/** Las hojas del fondo son más pequeñas que la delantera. */
export const BACK_SCALE = 0.82

/**
 * Los porcentajes van tipados como `${number}%` y no como `string` a propósito: es la forma que
 * `DimensionValue` de React Native acepta sin un cast. Que el tipo se escriba solo con literales
 * de plantilla es lo que deja al paquete sin depender de React Native para decirlo.
 */
export type BackPageLayout = {
  height: `${number}%`
  right: `${number}%`
  bottom: `${number}%`
  transformOrigin: 'center'
  transform: [{ rotate: `${number}deg` }]
}

/**
 * Da tamaño y posición a una hoja del fondo: la hace más pequeña, la rota y la ancla abajo-derecha
 * de modo que su *bounding box* rotado quepa justo dentro del contenedor —sin recortes ni
 * desbordes— sea cual sea el tamaño del contenedor.
 *
 * La trigonometría no es adorno: al rotar un rectángulo su caja envolvente crece
 * (`w·cos + h·sin`), así que una hoja a alto completo rotada 16° se sale de la tarjeta. El factor `fit`
 * es el alto máximo cuya caja rotada todavía entra.
 *
 * `aspect` es la forma REAL de la hoja, y por eso es un parámetro: en una hoja apaisada el ancho
 * pasa a ser la dimensión que limita, y una fórmula con la carta clavada adentro devolvería una
 * hoja que se sale de la tarjeta por los costados.
 */
export function backPageLayout(index: number, aspect: number = PAGE_ASPECT): BackPageLayout {
  const angle = index * STEP_ANGLE
  const rad = (angle * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))

  const maxByHeight = 1 / (cos + aspect * sin)
  const maxByWidth = 1 / (aspect * cos + sin)
  const fit = Math.min(maxByHeight, maxByWidth)

  const h = Math.min(PAGE_HEIGHT, fit) * BACK_SCALE
  const w = h * aspect

  const boxW = w * cos + h * sin
  const boxH = h * cos + w * sin

  return {
    height: `${h * 100}%`,
    right: `${((boxW - w) / 2) * 100}%`,
    bottom: `${((boxH - h) / 2) * 100}%`,
    transformOrigin: 'center',
    transform: [{ rotate: `${angle}deg` }]
  }
}

/**
 * El alto de la hoja DELANTERA, como fracción del contenedor.
 *
 * Existe por la misma razón que el `fit` de `backPageLayout`, sin la rotación: la delantera mide
 * el alto COMPLETO, y con la carta eso da un 77 % de ancho —entra cómoda—. Una hoja **apaisada**
 * con el mismo alto pediría un 140 % de ancho y se saldría de la celda por los costados. Se acota
 * al alto máximo cuyo ancho todavía entra: sólo las apaisadas se achican.
 */
export function frontPageHeight(aspect: number = PAGE_ASPECT): `${number}%` {
  const fit = aspect > 0 ? Math.min(PAGE_HEIGHT, 1 / aspect) : PAGE_HEIGHT
  return `${fit * 100}%`
}
